import {put, takeLatest, call} from 'redux-saga/effects';
import {SUCCESS_KEY} from 'constants/keys';
import {
  getBacklogs,
  addUserStory as addUserStoryAPI,
  getUserStoryDetails,
  updateStoryDetails as updateStoryDetailsAPI,
  deleteStory,
  validateUserStory as validateUserStoryAPI,
} from 'services/api';
import {
  getBacklogsFailure,
  getBacklogsRequest,
  getBacklogsSuccess,
  getBacklogsRequestParams,
  getBacklogsSuccessResponse,
  addUserStoryRequest,
  addUserStoryFailure,
  addUserStorySuccess,
  addUserStoryRequestPayload,
  addUserStorySucessResponse,
  getUserStoryDetailsParams,
  getUserStoryDetailsSuccessResponse,
  getUserStoryDetailsFailure,
  getUserStoryDetailsRequest,
  getUserStoryDetailsSuccess,
  updateUserStoryFailure,
  updateUserStoryRequest,
  updateUserStorySuccess,
  updateUserStoryDetailsPayload,
  updateUserStoryDetailsSuccessResponse,
  deleteUserStoryFailure,
  deleteUserStoryRequest,
  deleteUserStorySuccess,
  deleteUserStoryDetailsPayload,
  deleteUserStoryDetailsSuccessResponse,
  validateUserStoryRequest,
  validateUserStoryRequestPayload,
  validateUserStorySuccess,
  validateUserStorySuccessResponse,
  validateUserStoryFailure,
} from '../slices/userStorySlice';

/**
 * types for get backlogs
 */

type GetBacklogsRequestAction = {
  type: typeof getBacklogsRequest.type;
  payload: getBacklogsRequestParams;
};

type GetBacklogsResponse = {
  status: string;
} & getBacklogsSuccessResponse;

/**
 * types for add user story
 */

type AddUserStoryRequestAction = {
  type: typeof addUserStoryRequest.type;
  payload: addUserStoryRequestPayload;
};

type AddUserStoryResponse = {
  status: string;
} & addUserStorySucessResponse;

/**
 * type for view story details
 */

type GetUserStoryDetailsAction = {
  type: typeof getUserStoryDetailsRequest.type;
  payload: getUserStoryDetailsParams;
};

type GetUserStoryDetailsSuccess = {
  status: string;
} & getUserStoryDetailsSuccessResponse;

/**
 * type update for update story details
 */
type UpdateUserStoryDetailsAction = {
  type: typeof updateUserStoryRequest.type;
  payload: updateUserStoryDetailsPayload;
};

type UpdateUserStoryDetailsSuccess = {
  status: string;
} & updateUserStoryDetailsSuccessResponse;

/**
 * type for delete story details
 */
type DeleteUserStoryAction = {
  type: typeof deleteUserStoryRequest.type;
  payload: deleteUserStoryDetailsPayload;
};

type DeleteUserStorySuccess = {
  status: string;
} & deleteUserStoryDetailsSuccessResponse;

/**
 * type for validate user story
 */
type ValidateUserStoryAction = {
  type: typeof validateUserStoryRequest.type;
  payload: validateUserStoryRequestPayload;
};

type ValidateUserStoryResponse = {
  status: string;
} & validateUserStorySuccessResponse;

/**
 * generator functions
 */
function* backlogs(action: GetBacklogsRequestAction): Generator {
  try {
    // Use 'call' to handle the promise returned by demo
    const response: GetBacklogsResponse = yield call(
      getBacklogs,
      action.payload,
    );
    // Check response status and dispatch success or failure action
    if (response.status === SUCCESS_KEY) {
      yield put(getBacklogsSuccess(response));
    } else {
      yield put(getBacklogsFailure(response.status_code));
    }
  } catch (e) {
    yield put(getBacklogsFailure('E-10001')); // Replace with specific error code as needed
  }
}

function* addUserStory(action: AddUserStoryRequestAction): Generator {
  try {
    const response: AddUserStoryResponse = yield call(
      addUserStoryAPI,
      action.payload,
    );
    // Check response status and dispatch success or failure action
    if (response.status === SUCCESS_KEY) {
      yield put(addUserStorySuccess(response));
    } else {
      yield put(addUserStoryFailure(response.status_code));
    }
  } catch (e) {
    yield put(addUserStoryFailure('E-10001'));
  }
}

function* viewUserStoryDetails(action: GetUserStoryDetailsAction): Generator {
  try {
    const response: GetUserStoryDetailsSuccess = yield call(
      getUserStoryDetails,
      action.payload,
    );
    // Check response status and dispatch success or failure action
    if (response.status === SUCCESS_KEY) {
      yield put(getUserStoryDetailsSuccess(response));
    } else {
      yield put(getUserStoryDetailsFailure(response.status_code));
    }
  } catch (e) {
    yield put(getUserStoryDetailsFailure('E-10001'));
  }
}

function* updateUserStoryDetails(
  action: UpdateUserStoryDetailsAction,
): Generator {
  try {
    const response: UpdateUserStoryDetailsSuccess = yield call(
      updateStoryDetailsAPI,
      action.payload,
    );
    // Check response status and dispatch success or failure action
    if (response.status === SUCCESS_KEY) {
      yield put(updateUserStorySuccess(response));
    } else {
      yield put(updateUserStoryFailure(response.status_code));
    }
  } catch (e) {
    yield put(updateUserStoryFailure('E-10001'));
  }
}

function* deleteUserStory(action: DeleteUserStoryAction): Generator {
  try {
    const response: DeleteUserStorySuccess = yield call(
      deleteStory,
      action.payload,
    );
    // Check response status and dispatch success or failure action
    if (response.status === SUCCESS_KEY) {
      yield put(deleteUserStorySuccess(response));
    } else {
      yield put(deleteUserStoryFailure(response.status_code));
    }
  } catch (e) {
    yield put(deleteUserStoryFailure('E-10001'));
  }
}
/**
 * validate user story
 */
function* validateUserStory(action: ValidateUserStoryAction): Generator {
  try {
    const response: ValidateUserStoryResponse = yield call(
      validateUserStoryAPI,
      action.payload,
    );
    // Check response status and dispatch success or failure action
    if (response.status === SUCCESS_KEY) {
      yield put(validateUserStorySuccess(response));
    } else {
      yield put(validateUserStoryFailure(response.status_code));
    }
  } catch (e) {
    yield put(validateUserStoryFailure('E-10001'));
  }
}

// user story saga
export function* userStorySaga(): Generator {
  yield takeLatest(getBacklogsRequest.type, backlogs);
  yield takeLatest(addUserStoryRequest.type, addUserStory);
  yield takeLatest(getUserStoryDetailsRequest.type, viewUserStoryDetails);
  yield takeLatest(updateUserStoryRequest.type, updateUserStoryDetails);
  yield takeLatest(deleteUserStoryRequest.type, deleteUserStory);
  yield takeLatest(validateUserStoryRequest.type, validateUserStory);
}
